import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { getBaseURL } from "../../services/api";
import { ToastProvider, useToast } from "./AdminToast";
import { S, COLORS, GLOBAL_ADMIN_CSS } from "./adminStyles";

const API_URL = getBaseURL();

const AdminUserActivityInner = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);
  const [fullProfile, setFullProfile] = useState(null);
  const [profileLoading, setProfileLoading] = useState(false);
  const [txFilter, setTxFilter] = useState('ALL'); // ALL, CREDIT, DEBIT
  
  const token = localStorage.getItem("adminToken");
  const navigate = useNavigate();
  const toast = useToast();

  useEffect(() => {
    if (!token) navigate("/winrox11-super-admin");
    else fetchAllUsers();
  }, [token, navigate]);

  const fetchAllUsers = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/admin/users`, { headers: { Authorization: `Bearer ${token}` } });
      setUsers(res.data);
    } catch (err) {
      toast.error("Failed to load users list.");
    } finally { setLoading(false); }
  };

  const fetchFullProfile = async (userId) => {
    setProfileLoading(true);
    setSelectedUser(userId);
    try {
      const res = await axios.get(`${API_URL}/admin/users/${userId}/full-profile`, { headers: { Authorization: `Bearer ${token}` } });
      setFullProfile(res.data);
    } catch (err) {
      toast.error("Failed to load full profile.");
    } finally { setProfileLoading(false); }
  };

  const filteredUsers = users.filter(u => {
    const name = `${u.first_name} ${u.last_name || ""}`.toLowerCase();
    const mobile = (u.mobile || "").toLowerCase();
    return !search || name.includes(search.toLowerCase()) || mobile.includes(search.toLowerCase());
  });

  const handleBackToUsers = () => {
    setSelectedUser(null);
    setFullProfile(null);
  };

  if (selectedUser && fullProfile) {
    const { user, bank, transactions, activity } = fullProfile;
    return (
      <div style={S.page}>
        <style>{GLOBAL_ADMIN_CSS}</style>
        <button onClick={handleBackToUsers} style={S.backBtn}>← Back to User List</button>
        
        <div style={S.topBar}>
          <div>
            <div style={S.pageTitle}>👤 {user.first_name} {user.last_name}</div>
            <div style={S.pageSubtitle}>{user.mobile} • Member since {new Date(user.created_at).toLocaleDateString()}</div>
          </div>
          <div style={{ background: COLORS.green + "15", border: `1px solid ${COLORS.green}30`, padding: "10px 20px", borderRadius: "12px", textAlign: "right" }}>
            <div style={{ fontSize: "10px", color: COLORS.textMuted, fontWeight: 700, textTransform: "uppercase" }}>Wallet Balance</div>
            <div style={{ fontSize: "24px", fontWeight: 800, color: COLORS.green }}>₹{user.wallet}</div>
          </div>
        </div>

        {/* 🏦 BANK DETAILS SECTION */}
        <div style={S.sectionTitle}>🏦 Bank Account Info</div>
        <div style={{ background: COLORS.card, border: `1px solid ${COLORS.cardBorder}`, borderRadius: "16px", padding: "20px", marginBottom: "30px" }}>
            {bank ? (
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "20px" }}>
                    <div>
                        <div style={{ fontSize: "10px", color: COLORS.textMuted, fontWeight: 700, textTransform: "uppercase" }}>Holder Name</div>
                        <div style={{ fontSize: "16px", fontWeight: 700 }}>{bank.holder_name}</div>
                    </div>
                    <div>
                        <div style={{ fontSize: "10px", color: COLORS.textMuted, fontWeight: 700, textTransform: "uppercase" }}>Account Number</div>
                        <div style={{ fontSize: "16px", fontWeight: 700, color: COLORS.indigoLight }}>{bank.account_number}</div>
                    </div>
                    <div>
                        <div style={{ fontSize: "10px", color: COLORS.textMuted, fontWeight: 700, textTransform: "uppercase" }}>IFSC Code</div>
                        <div style={{ fontSize: "16px", fontWeight: 700 }}>{bank.ifsc}</div>
                    </div>
                    <div>
                        <div style={{ fontSize: "10px", color: COLORS.textMuted, fontWeight: 700, textTransform: "uppercase" }}>Bank Name</div>
                        <div style={{ fontSize: "16px", fontWeight: 700 }}>{bank.bank_name}</div>
                    </div>
                </div>
            ) : (
                <div style={{ color: COLORS.textMuted, fontStyle: "italic", textAlign: "center", padding: "10px" }}>Bank details not submitted yet.</div>
            )}
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "24px", alignItems: "start" }}>
            {/* 🏆 ACTIVITY LOG */}
            <div>
                <div style={S.sectionTitle}>🏆 Contest Activity (Recent First)</div>
                <div style={{ background: COLORS.card, border: `1px solid ${COLORS.cardBorder}`, borderRadius: "16px", overflow: "hidden" }}>
                    {activity.length === 0 ? (
                        <div style={{ padding: "30px", textAlign: "center", color: COLORS.textMuted }}>No contest activity found.</div>
                    ) : (
                        <table style={{ width: "100%", borderCollapse: "collapse" }}>
                            <thead>
                                <tr style={{ background: COLORS.inputBg }}>
                                    <th style={{ padding: "12px 16px", fontSize: "10px", color: COLORS.textMuted, textAlign: "left" }}>MATCH / CONTEST</th>
                                    <th style={{ padding: "12px 16px", fontSize: "10px", color: COLORS.textMuted, textAlign: "right" }}>DATE</th>
                                </tr>
                            </thead>
                            <tbody>
                                {activity.map((a, idx) => (
                                    <tr key={idx} style={{ borderBottom: `1px solid ${COLORS.cardBorder}` }}>
                                        <td style={{ padding: "12px 16px" }}>
                                            <div style={{ fontWeight: 700, fontSize: "13px" }}>{a.team1} vs {a.team2}</div>
                                            <div style={{ fontSize: "11px", color: COLORS.indigoLight }}>{a.contest_title} (Entry: ₹{a.entry_fee})</div>
                                        </td>
                                        <td style={{ padding: "12px 16px", textAlign: "right", fontSize: "11px", color: COLORS.textMuted }}>
                                            {new Date(a.joined_at).toLocaleString()}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>

            {/* 💸 WALLET TRANSACTIONS */}
            <div>
                <div style={S.sectionTitle}>💸 Wallet Transactions</div>
                
                {/* Category Totals */}
                <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "10px", marginBottom: "16px" }}>
                    {[
                        { label: "Deposits", color: COLORS.green, type: 'DEPOSIT' },
                        { label: "Withdraws", color: COLORS.red, type: 'WITHDRAW' },
                        { label: "Contests", color: COLORS.amber, type: 'CONTEST' },
                        { label: "Winnings", color: COLORS.indigoLight, type: 'WINNING' }
                    ].map(cat => {
                        const total = transactions.reduce((sum, t) => {
                            const isMatch = (cat.type === 'DEPOSIT' && t.type === 'CREDIT' && !t.description?.toLowerCase().includes('won')) ||
                                            (cat.type === 'WITHDRAW' && t.type === 'DEBIT' && t.description?.toLowerCase().includes('withdraw')) ||
                                            (cat.type === 'CONTEST' && t.type === 'DEBIT' && (t.description?.toLowerCase().includes('contest') || t.description?.toLowerCase().includes('joined'))) ||
                                            (cat.type === 'WINNING' && (t.type === 'winning' || (t.type === 'CREDIT' && t.description?.toLowerCase().includes('won'))));
                            return isMatch ? sum + Number(t.amount) : sum;
                        }, 0);
                        return (
                            <div key={cat.label} style={{ background: COLORS.card, border: `1px solid ${cat.color}30`, borderRadius: "12px", padding: "10px", textAlign: "center" }}>
                                <div style={{ fontSize: "14px", fontWeight: 800, color: cat.color }}>₹{total.toLocaleString("en-IN", { maximumFractionDigits: 0 })}</div>
                                <div style={{ fontSize: "8px", color: COLORS.textMuted, fontWeight: 700, textTransform: "uppercase", marginTop: "2px" }}>{cat.label}</div>
                            </div>
                        );
                    })}
                </div>

                <div style={{ background: COLORS.card, border: `1px solid ${COLORS.cardBorder}`, borderRadius: "16px", overflow: "hidden" }}>
                    {/* Category Tabs */}
                    <div style={{ display: "flex", borderBottom: `1px solid ${COLORS.cardBorder}`, background: COLORS.inputBg, overflowX: "auto" }}>
                        {['ALL', 'DEPOSIT', 'WITHDRAW', 'CONTEST', 'WINNING'].map(tab => (
                            <button
                                key={tab}
                                onClick={() => setTxFilter(tab)}
                                style={{
                                    flex: 1,
                                    padding: "12px 8px",
                                    minWidth: "80px",
                                    background: txFilter === tab ? COLORS.card : "transparent",
                                    border: "none",
                                    color: txFilter === tab ? COLORS.indigoLight : COLORS.textMuted,
                                    fontSize: "9px",
                                    fontWeight: 700,
                                    cursor: "pointer",
                                    textTransform: "uppercase",
                                    letterSpacing: "0.5px",
                                    borderBottom: txFilter === tab ? `2px solid ${COLORS.indigoLight}` : "none"
                                }}
                            >
                                {tab === 'ALL' ? 'History' : tab.charAt(0) + tab.slice(1).toLowerCase()}
                            </button>
                        ))}
                    </div>

                    {transactions.filter(t => {
                        if (txFilter === 'ALL') return true;
                        if (txFilter === 'DEPOSIT') return t.type === 'CREDIT' && !t.description?.toLowerCase().includes('won');
                        if (txFilter === 'WITHDRAW') return t.type === 'DEBIT' && t.description?.toLowerCase().includes('withdraw');
                        if (txFilter === 'CONTEST') return t.type === 'DEBIT' && (t.description?.toLowerCase().includes('contest') || t.description?.toLowerCase().includes('joined'));
                        if (txFilter === 'WINNING') return t.type === 'winning' || (t.type === 'CREDIT' && t.description?.toLowerCase().includes('won'));
                        return false;
                    }).length === 0 ? (
                        <div style={{ padding: "30px", textAlign: "center", color: COLORS.textMuted }}>No transactions found in this category.</div>
                    ) : (
                        <table style={{ width: "100%", borderCollapse: "collapse" }}>
                            <thead>
                                <tr style={{ background: COLORS.inputBg }}>
                                    <th style={{ padding: "12px 16px", fontSize: "10px", color: COLORS.textMuted, textAlign: "left" }}>DESCRIPTION / DATE</th>
                                    <th style={{ padding: "12px 16px", fontSize: "10px", color: COLORS.textMuted, textAlign: "right" }}>AMOUNT</th>
                                </tr>
                            </thead>
                            <tbody>
                                {transactions.filter(t => {
                                    if (txFilter === 'ALL') return true;
                                    if (txFilter === 'DEPOSIT') return t.type === 'CREDIT' && !t.description?.toLowerCase().includes('won');
                                    if (txFilter === 'WITHDRAW') return t.type === 'DEBIT' && t.description?.toLowerCase().includes('withdraw');
                                    if (txFilter === 'CONTEST') return t.type === 'DEBIT' && (t.description?.toLowerCase().includes('contest') || t.description?.toLowerCase().includes('joined'));
                                    if (txFilter === 'WINNING') return t.type === 'winning' || (t.type === 'CREDIT' && t.description?.toLowerCase().includes('won'));
                                    return false;
                                }).map((t, idx) => (
                                    <tr key={idx} style={{ borderBottom: `1px solid ${COLORS.cardBorder}` }}>
                                        <td style={{ padding: "12px 16px" }}>
                                            <div style={{ fontWeight: 600, fontSize: "12px", color: COLORS.text }}>{t.description || "Transaction"}</div>
                                            <div style={{ fontSize: "10px", color: COLORS.textMuted }}>{new Date(t.created_at).toLocaleString("en-IN", { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}</div>
                                            {t.utr && <div style={{ fontSize: "9px", color: COLORS.indigoLight, fontWeight: 700, marginTop: "2px" }}>UTR: {t.utr}</div>}
                                        </td>
                                        <td style={{ padding: "12px 16px", textAlign: "right" }}>
                                            <div style={{ fontWeight: 800, color: (t.type === 'CREDIT' || t.type === 'winning') ? COLORS.green : COLORS.red }}>
                                                {(t.type === 'CREDIT' || t.type === 'winning') ? '+' : '-'}₹{t.amount}
                                            </div>
                                            <div style={{ fontSize: "9px", color: COLORS.textMuted, textTransform: "uppercase", fontWeight: 700 }}>{t.status}</div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>
        </div>
      </div>
    );
  }

  return (
    <div style={S.page}>
      <style>{GLOBAL_ADMIN_CSS}</style>
      <button onClick={() => navigate("/winrox11-super-admin/dashboard")} style={S.backBtn}>← Back to Dashboard</button>

      <div style={S.topBar}>
        <div>
          <div style={S.pageTitle}>🕵️ User Profiles & Activity</div>
          <div style={S.pageSubtitle}>Search any user to view their complete master history</div>
        </div>
      </div>

      {/* Search Filter */}
      <div style={{ marginBottom: "24px" }}>
        <input
          className="admin-input-focus"
          style={{ ...S.input, maxWidth: "500px", height: "48px", fontSize: "16px" }}
          placeholder="🔍 Search user by name or mobile number..."
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
      </div>

      {loading ? (
        <div style={S.loading}>Loading users list...</div>
      ) : filteredUsers.length === 0 ? (
        <div style={S.empty}>No users found.</div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))", gap: "20px" }}>
          {filteredUsers.map(user => (
            <div 
              key={user.id} 
              className="admin-card-hover" 
              onClick={() => fetchFullProfile(user.id)}
              style={{ background: COLORS.card, border: `1px solid ${COLORS.cardBorder}`, borderRadius: "16px", padding: "20px", cursor: "pointer", transition: "all 0.2s" }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: "14px", marginBottom: "15px" }}>
                <div style={{ width: "56px", height: "56px", borderRadius: "16px", background: COLORS.indigo + "15", border: `1px solid ${COLORS.indigo}30`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: "24px" }}>👤</div>
                <div>
                  <div style={{ fontWeight: 800, color: COLORS.text, fontSize: "18px" }}>{user.first_name} {user.last_name}</div>
                  <div style={{ fontSize: "13px", color: COLORS.textMuted }}>{user.mobile}</div>
                </div>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", borderTop: `1px solid ${COLORS.cardBorder}`, paddingTop: "15px" }}>
                <div style={{ fontSize: "14px", fontWeight: 800, color: COLORS.green }}>₹{user.wallet}</div>
                <div style={{ color: COLORS.indigo, fontWeight: 700, fontSize: "13px" }}>Master Profile →</div>
              </div>
            </div>
          ))}
        </div>
      )}
      
      {profileLoading && (
          <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 10000, color: "white", fontWeight: 700 }}>
              🚀 Fetching Master Data...
          </div>
      )}
    </div>
  );
};

const AdminUserActivity = () => (
  <ToastProvider>
    <AdminUserActivityInner />
  </ToastProvider>
);

export default AdminUserActivity;
